import { z } from "zod";
import { ChartTypeSchema } from "./kpiChart.js";
import { MyTasksParamsSchema, defaultMyTasksParams } from "./myTasks.js";
import { QuickAccessParamsSchema, defaultQuickAccessParams, } from "./homeNavigation.js";
import { KaizenHealthParamsSchema, KaizenTableParamsSchema, defaultKaizenHealthParams, defaultKaizenTableParams, } from "./dashboardKaizen.js";
import { SolveActionsParamsSchema, defaultSolveActionsParams, } from "./solveActions.js";
import { LamaMaturityProfileParamsSchema, LamaMaturityTrendParamsSchema, LamaParticipationTrendParamsSchema, LamaPillarResultsParamsSchema, LamaPriorityRoadmapParamsSchema, LamaReportOverviewParamsSchema, LamaTeamHeatmapParamsSchema, LamaTopMoversParamsSchema, LamaMaturityHierarchyParamsSchema, LamaHierarchyResultsParamsSchema, } from "./lamaReport.js";
/**
 * Presentation-only params for `singleKpi`/`chart` on a dashboard instance:
 * the pinned `kpiId` lives in the `kpi` slot's connection parameters, never
 * here (D21/§10.4 — the same identifier must not appear in both places).
 */
export const KpiChartPresentationParamsSchema = z
    .object({
    chartType: ChartTypeSchema.optional(),
    showDelta: z.boolean().optional(),
    showCommentary: z.boolean().optional(),
})
    .strict();
const EmptyParamsSchema = z.object({}).strict();
const ALL_WIDTHS = ["small", "medium", "wide", "full"];
const WIDE_FULL = ["wide", "full"];
const MEDIUM_UP = ["medium", "wide", "full"];
const PERSONAL = ["personal_home"];
const PERSONAL_AND_VSM = ["personal_home", "vsm_home"];
const LAMA = ["lama_report"];
const NOT_CUSTOMIZABLE = {
    titleOverride: false,
    params: false,
    removable: true,
    connectionSelection: "none",
};
/**
 * Retitleable AND configurable (PB-191 Task 19) — the four LAMA sections whose
 * projector already hardcoded a presentation constant.
 *
 * `params: true` is only honest where an `EditorForm` exists to author one and a
 * render path honours it; the parity spec enforces that pairing, because
 * `params: true` with no editor is dead policy and the reverse 422s.
 */
const RETITLE_AND_PARAMS = {
    titleOverride: true,
    params: true,
    removable: true,
    connectionSelection: "none",
};
/**
 * Retitleable, but nothing else (PB-191 Task 18) — every LAMA report section.
 *
 * A SIBLING OF `NOT_CUSTOMIZABLE` RATHER THAN AN EDIT TO IT: that constant is
 * shared with eight non-LAMA kinds (`mySolves`, `myVsms`, `projectPulse`,
 * `strategicHealth`, `dailyBrief`, `coachingNudge`, `moodCheck`,
 * `kaizenHealth`), and widening it would grant them a title override nobody
 * asked for — silently, because their defs would then FAIL the def/contract
 * parity spec rather than gaining an affordance.
 *
 * `params: false` stays, and it is not a placeholder for Task 19. It is what
 * makes the state coherent today: no LAMA kind registers an `EditorForm`, so
 * there is no way to author a param, and `dashboard-widgets.service.ts` 422s a
 * params PATCH for a kind declaring `params: false` — a `true` here would be
 * policy permitting something nothing can produce.
 *
 * A precision worth keeping, because the obvious reading is wrong in BOTH
 * directions: `WidgetBodyProps` carries no `params` field, so a saved param
 * cannot reach a widget body directly — but that does not make presentation
 * params impossible. `myTasks` persists one and receives it through `data`
 * ("the persisted default rides the params via the editor"), i.e. the HOST
 * resolves params into the view model. So the LAMA render path could honour a
 * param without changing any shared type. What is missing is not a mechanism;
 * it is a decision about which knobs exist. The def/contract parity spec ties `params` to the presence of
 * an `EditorForm` for exactly that reason — "an EditorForm whose kind declares
 * `params: false` produces a PATCH the backend rejects 422, and `params: true`
 * with no editor is dead policy". Flipping this to `true` without an editor form
 * AND a render path is the dead-policy half of that sentence.
 *
 * `connectionSelection: "none"` is untouched: LAMA's data connections are
 * template-owned (ADR-0054), and a title is presentation, not a binding.
 */
const RETITLE_ONLY = {
    titleOverride: true,
    params: false,
    removable: true,
    connectionSelection: "none",
};
export const DASHBOARD_WIDGET_CONTRACTS = new Map([
    // `chart` is personal_home-only: kpi/kpi-snapshot@1 requires a selected
    // kpiId while D21 disables connection selection outside Personal Home.
    // `singleKpi` does run on VSM Home through subject-scoped
    // vsm/linked-kpi@1; effectiveConnectionSelection deliberately lets an
    // editor choose one of that VSM's linked KPIs.
    {
        kind: "singleKpi",
        schemaVersion: 1,
        paramsSchema: KpiChartPresentationParamsSchema,
        defaultParams: { chartType: "big-number" },
        allowedDashboardTypes: PERSONAL_AND_VSM,
        availableForNewInstances: true,
        defaultWidth: "medium",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: true,
        customization: {
            titleOverride: true,
            params: true,
            removable: true,
            connectionSelection: "approved_domain_object",
        },
        slots: {
            kpi: {
                required: true,
                accepts: ["kpi/kpi-snapshot@1", "vsm/linked-kpi@1"],
            },
        },
    },
    {
        kind: "chart",
        schemaVersion: 1,
        paramsSchema: KpiChartPresentationParamsSchema,
        defaultParams: { chartType: "line" },
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: false,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: true,
        customization: {
            titleOverride: true,
            params: true,
            removable: true,
            connectionSelection: "approved_domain_object",
        },
        slots: { kpi: { required: true, accepts: ["kpi/kpi-snapshot@1"] } },
    },
    // `params: true` on the two KPI-list kinds despite a `{}`-strict schema:
    // their editor produces a params PATCH alongside the connection change,
    // and `params: false` rejected every one of those edits with 422. An
    // empty strict schema makes the accepted PATCH a no-op, so this is the
    // safe half of the pair — the parity spec pins EditorForm presence to
    // this flag so the two registries cannot drift apart again.
    {
        kind: "kpiWatchlist",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL_AND_VSM,
        availableForNewInstances: false,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: {
            titleOverride: true,
            params: true,
            removable: true,
            connectionSelection: "approved_domain_object",
        },
        slots: {
            kpis: {
                required: true,
                accepts: ["kpi/kpi-list@1", "vsm/linked-kpis@1"],
            },
        },
    },
    {
        kind: "kpiTable",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL_AND_VSM,
        availableForNewInstances: true,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: {
            titleOverride: true,
            params: true,
            removable: true,
            connectionSelection: "approved_domain_object",
        },
        slots: {
            kpis: {
                required: true,
                accepts: ["kpi/kpi-list@1", "vsm/linked-kpis@1"],
            },
        },
    },
    {
        kind: "myTasks",
        schemaVersion: 1,
        paramsSchema: MyTasksParamsSchema,
        defaultParams: defaultMyTasksParams,
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: false,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: {
            titleOverride: true,
            params: true,
            removable: true,
            connectionSelection: "none",
        },
        slots: { tasks: { required: true, accepts: ["solve/my-tasks@1"] } },
    },
    /* `allowMultiple: true` is the point of this kind: the agreed product
       behaviour is several independently configured instances on one Home
       (one per SOLVE, or one per source), so the singleton rule that governs
       `mySolves`/`kaizenTable` would defeat it. Everything else follows
       `kaizenTable`: a bounded-parameter connection edited by a registered
       EditorForm, a title override, and presentation-only instance params. */
    {
        kind: "solveActions",
        schemaVersion: 1,
        paramsSchema: SolveActionsParamsSchema,
        defaultParams: defaultSolveActionsParams,
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: true,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: true,
        customization: {
            titleOverride: true,
            params: true,
            removable: true,
            connectionSelection: "bounded_parameters",
        },
        slots: {
            actions: { required: true, accepts: ["solve/solve-actions@1"] },
        },
    },
    {
        kind: "mySolves",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: true,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            solves: {
                required: true,
                accepts: ["solve/my-active-solves@1"],
            },
        },
    },
    {
        kind: "myVsms",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: true,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            vsms: { required: true, accepts: ["vsm/my-visible-vsms@1"] },
        },
    },
    {
        kind: "quickAccess",
        schemaVersion: 1,
        paramsSchema: QuickAccessParamsSchema,
        defaultParams: defaultQuickAccessParams,
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: true,
        defaultWidth: "medium",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: {
            titleOverride: false,
            params: true,
            removable: true,
            connectionSelection: "none",
        },
        // Host-context widgets are valid zero-slot contracts. The shell
        // supplies only its authorized, code-backed module catalogue.
        slots: {},
    },
    {
        kind: "projectPulse",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: false,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            projects: { required: true, accepts: ["solve/project-pulse@1"] },
        },
    },
    {
        kind: "strategicHealth",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: false,
        defaultWidth: "full",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            summary: {
                required: true,
                accepts: ["hoshin/strategic-health@1"],
            },
        },
    },
    {
        kind: "dailyBrief",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: false,
        defaultWidth: "full",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            brief: { required: true, accepts: ["player-ai/daily-brief@1"] },
        },
    },
    {
        kind: "coachingNudge",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: false,
        defaultWidth: "medium",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            nudge: { required: true, accepts: ["player-ai/coaching-nudge@1"] },
        },
    },
    {
        kind: "moodCheck",
        schemaVersion: 1,
        paramsSchema: EmptyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: PERSONAL,
        availableForNewInstances: true,
        defaultWidth: "medium",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            mood: { required: true, accepts: ["personal-state/mood-today@1"] },
        },
    },
    {
        kind: "kaizenTable",
        schemaVersion: 1,
        paramsSchema: KaizenTableParamsSchema,
        defaultParams: defaultKaizenTableParams,
        allowedDashboardTypes: PERSONAL_AND_VSM,
        availableForNewInstances: true,
        defaultWidth: "wide",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: {
            titleOverride: true,
            params: true,
            removable: true,
            connectionSelection: "bounded_parameters",
        },
        slots: {
            kaizens: {
                required: true,
                accepts: ["kaizen/personal-list@3", "kaizen/vsm-list@3"],
            },
        },
    },
    {
        kind: "kaizenHealth",
        schemaVersion: 1,
        paramsSchema: KaizenHealthParamsSchema,
        defaultParams: defaultKaizenHealthParams,
        allowedDashboardTypes: ["vsm_home"],
        availableForNewInstances: true,
        defaultWidth: "medium",
        allowedWidths: ALL_WIDTHS,
        allowMultiple: false,
        customization: NOT_CUSTOMIZABLE,
        slots: {
            health: { required: true, accepts: ["kaizen/vsm-health@2"] },
        },
    },
    // ---- LAMA report sections (plan §13.3) ----
    {
        kind: "lamaReportOverview",
        schemaVersion: 1,
        paramsSchema: LamaReportOverviewParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_ONLY,
        slots: {
            summary: { required: true, accepts: ["lama/report-overview@1"] },
        },
    },
    {
        kind: "lamaMaturityProfile",
        schemaVersion: 1,
        paramsSchema: LamaMaturityProfileParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_ONLY,
        slots: {
            profile: { required: true, accepts: ["lama/maturity-profile@1"] },
        },
    },
    {
        kind: "lamaMaturityTrend",
        schemaVersion: 1,
        paramsSchema: LamaMaturityTrendParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "wide",
        allowedWidths: MEDIUM_UP,
        allowMultiple: false,
        customization: RETITLE_AND_PARAMS,
        slots: {
            trend: { required: true, accepts: ["lama/maturity-trend@1"] },
        },
    },
    {
        kind: "lamaParticipationTrend",
        schemaVersion: 1,
        paramsSchema: LamaParticipationTrendParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "wide",
        allowedWidths: MEDIUM_UP,
        allowMultiple: false,
        customization: RETITLE_AND_PARAMS,
        slots: {
            trend: { required: true, accepts: ["lama/participation-trend@1"] },
        },
    },
    {
        kind: "lamaCategoryResults",
        schemaVersion: 1,
        paramsSchema: LamaPillarResultsParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_ONLY,
        slots: {
            /* PB-191 Task 2a: BOTH refs, on BOTH kinds. Task 4 rewrites
               `widget_kind` and the connection row in one hook, but a rolling
               PostSync leaves the two briefly out of step, so every one of the
               four (kind x ref) states has to resolve rather than error. */
            rows: {
                required: true,
                accepts: ["lama/category-results@2", "lama/pillar-results@1"],
            },
        },
    },
    {
        /* 2026-09-10 ADR-0027 amendment: the v3 hierarchy, both sources. ONE
           slot on ONE bounded dataset — the baseline/responses/compare choice
           is host `?view=` state applied at render, never a params field or a
           data id (PEND2). */
        kind: "lamaMaturityHierarchy",
        schemaVersion: 1,
        paramsSchema: LamaMaturityHierarchyParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_ONLY,
        slots: {
            hierarchy: {
                required: true,
                accepts: ["lama/maturity-hierarchy@1"],
            },
        },
    },
    {
        kind: "lamaHierarchyResults",
        schemaVersion: 1,
        paramsSchema: LamaHierarchyResultsParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_ONLY,
        slots: {
            hierarchy: {
                required: true,
                accepts: ["lama/maturity-hierarchy@1"],
            },
        },
    },
    {
        /* PB-191 D35: the pre-rename kind. `availableForNewInstances: false` is
           what makes this an expand seam rather than a duplicate menu entry —
           "Hidden kinds remain renderable but cannot be added as new
           instances", which is exactly the tolerance an un-migrated row needs
           while Task 4's PostSync `UPDATE` has not run (and after a rollback,
           where Prisma gives no way back). Retired by Task 33. */
        kind: "lamaPillarResults",
        schemaVersion: 1,
        paramsSchema: LamaPillarResultsParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: false,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_ONLY,
        slots: {
            /* PB-191 Task 2a: BOTH refs, on BOTH kinds. Task 4 rewrites
               `widget_kind` and the connection row in one hook, but a rolling
               PostSync leaves the two briefly out of step, so every one of the
               four (kind x ref) states has to resolve rather than error. */
            rows: {
                required: true,
                accepts: ["lama/category-results@2", "lama/pillar-results@1"],
            },
        },
    },
    {
        kind: "lamaTeamHeatmap",
        schemaVersion: 1,
        paramsSchema: LamaTeamHeatmapParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: false,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_ONLY,
        slots: {
            heatmap: { required: true, accepts: ["lama/team-heatmap@1"] },
        },
    },
    {
        kind: "lamaTopMovers",
        schemaVersion: 1,
        paramsSchema: LamaTopMoversParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_AND_PARAMS,
        slots: {
            movers: { required: true, accepts: ["lama/top-movers@1"] },
        },
    },
    {
        kind: "lamaPriorityRoadmap",
        schemaVersion: 1,
        paramsSchema: LamaPriorityRoadmapParamsSchema,
        defaultParams: {},
        allowedDashboardTypes: LAMA,
        availableForNewInstances: true,
        defaultWidth: "full",
        allowedWidths: WIDE_FULL,
        allowMultiple: false,
        customization: RETITLE_AND_PARAMS,
        slots: {
            roadmap: { required: true, accepts: ["lama/priority-roadmap@1"] },
        },
    },
].map((contract) => [contract.kind, contract]));
export function getDashboardWidgetContract(kind) {
    return DASHBOARD_WIDGET_CONTRACTS.get(kind);
}
/** The add-menu catalogue for one dashboard type. */
export function dashboardWidgetKindsForType(dashboardType) {
    return [...DASHBOARD_WIDGET_CONTRACTS.values()]
        .filter((c) => c.availableForNewInstances &&
        c.allowedDashboardTypes.includes(dashboardType))
        .map((c) => c.kind);
}
/**
 * Bindings remain template/contract-owned. Personal Home exposes only each
 * contract's bounded selection. VSM additionally permits the two approved
 * subject-scoped choices: a linked KPI and Kaizen filters. LAMA is fixed.
 */
export function effectiveConnectionSelection(dashboardType, contract) {
    if (dashboardType === "personal_home") {
        return contract.customization.connectionSelection;
    }
    if (dashboardType === "vsm_home" &&
        (contract.kind === "singleKpi" || contract.kind === "kaizenTable")) {
        return contract.customization.connectionSelection;
    }
    return "none";
}
/** Validate presentation-only instance params. Throws ZodError on mismatch. */
export function validateDashboardWidgetParams(kind, raw) {
    const contract = DASHBOARD_WIDGET_CONTRACTS.get(kind);
    if (!contract) {
        throw new Error(`No dashboard widget contract for kind '${kind}'.`);
    }
    return contract.paramsSchema.parse(raw);
}
//# sourceMappingURL=dashboard-contracts.js.map